<script lang="ts">
	import "#lib/assets/app.css";
	import "@fontsource-variable/inter";
	import "@fontsource-variable/instrument-sans";
	import { setContext } from "svelte";
	import { writable, type Writable } from "svelte/store";
	import { AbacusClient } from "#lib/api/abacus.js";
	import type { ClientOptions } from "#lib/api/api.js";
	import { AudiencesClient } from "#lib/api/audiences.js";
	import { AuditClient } from "#lib/api/audit.js";
	import { BillingClient } from "#lib/api/billing.js";
	import { BrandClient } from "#lib/api/brands.js";
	import { BundleClient } from "#lib/api/bundles.js";
	import { CodesClient } from "#lib/api/codes.js";
	import { ComplianceClient } from "#lib/api/compliance.js";
	import { ConversionsDevelopersClient } from "#lib/api/conversions-developers.js";
	import { EntrypointsClient } from "#lib/api/entrypoints.js";
	import { EventClient } from "#lib/api/events.js";
	import { FolderClient } from "#lib/api/folder.js";
	import { IdentitiesClient } from "#lib/api/identities.js";
	import { IntegrationsClient } from "#lib/api/integrations.js";
	import { LegalClient } from "#lib/api/legal.js";
	import { LinksClient } from "#lib/api/links.js";
	import { NotificationTemplateClient } from "#lib/api/notification-templates.js";
	import { NotificationClient } from "#lib/api/notifications.js";
	import { OnboardingClient } from "#lib/api/onboarding.js";
	import { PageClient } from "#lib/api/pages.js";
	import { PixelTrackerClient } from "#lib/api/pixel-tracker.js";
	import { PollsClient } from "#lib/api/polls.js";
	import { ProductClient } from "#lib/api/products.js";
	import { RegistryClient } from "#lib/api/registry.js";
	import { ReportsClient } from "#lib/api/reports.js";
	import { ScanabilityClient } from "#lib/api/scanability.js";
	import { SearchClient } from "#lib/api/search.js";
	import { ShortUrlsClient } from "#lib/api/shortUrls.js";
	import { StaticAssetClient } from "#lib/api/static-assets.js";
	import { StatusClient } from "#lib/api/status.js";
	import { TagClient } from "#lib/api/tags.js";
	import { WebhooksClient } from "#lib/api/webhooks.js";
	import { USE_PERMALINK } from "#lib/conversion.js";
	import { beforeNavigate } from "$app/navigation";
	import { updated } from "$app/state";
	import type { LayoutData } from "./$types";

	export let data: LayoutData;

	const clientOptions: Writable<ClientOptions> = writable(data.clientOptions);
	$: clientOptions.set(data.clientOptions);

	function getClientOptions(includeCookies: boolean = false) {
		return () => {
			return { ...$clientOptions, includeCookies };
		};
	}

	setContext("audiencesClient", new AudiencesClient(getClientOptions()));
	setContext("auditClient", new AuditClient(getClientOptions()));
	setContext("billingClient", new BillingClient(getClientOptions()));
	setContext("bundleClient", new BundleClient(getClientOptions(true)));
	setContext("pageClient", new PageClient(getClientOptions()));
	setContext("registryClient", new RegistryClient(getClientOptions()));
	setContext("staticAssetClient", new StaticAssetClient(getClientOptions()));
	setContext("brandClient", new BrandClient(getClientOptions()));
	setContext("folderClient", new FolderClient(getClientOptions()));
	setContext("tagClient", new TagClient(getClientOptions()));
	setContext("productClient", new ProductClient(getClientOptions(true)));
	setContext("codesClient", new CodesClient(getClientOptions()));
	setContext("entrypointsClient", new EntrypointsClient(getClientOptions()));
	setContext("statusClient", new StatusClient(getClientOptions()));
	setContext("shortUrlsClient", new ShortUrlsClient(getClientOptions()));
	setContext("searchClient", new SearchClient(getClientOptions()));
	setContext("linksClient", new LinksClient(getClientOptions()));
	setContext("identitiesClient", new IdentitiesClient(getClientOptions()));
	setContext("pixelTrackerClient", new PixelTrackerClient(getClientOptions()));
	setContext("webhooksClient", new WebhooksClient(getClientOptions()));
	setContext("integrationsClient", new IntegrationsClient(getClientOptions()));
	setContext("abacusClient", new AbacusClient(getClientOptions()));
	setContext("notificationTemplateClient", new NotificationTemplateClient(getClientOptions()));
	setContext("notificationClient", new NotificationClient(getClientOptions()));
	setContext("conversionsDevelopersClient", new ConversionsDevelopersClient(getClientOptions()));
	setContext("scanabilityClient", new ScanabilityClient(getClientOptions()));
	setContext("reportsClient", new ReportsClient(getClientOptions()));
	setContext("legalClient", new LegalClient(getClientOptions()));
	setContext("onboardingClient", new OnboardingClient(getClientOptions()));
	setContext("eventClient", new EventClient(getClientOptions()));
	setContext("complianceClient", new ComplianceClient(getClientOptions()));
	setContext("pollsClient", new PollsClient(getClientOptions()));

	setContext("flags", data.flags);

	// When version polling has detected a newer build, fall back to a full-page navigation so the browser
	// fetches the new build's chunks instead of lazy-loading ones the deploy removed.
	beforeNavigate(({ willUnload, to, shallow, type }) => {
		if (shallow && type === "goto") return;

		if (updated.current && !willUnload && to?.url) {
			location.href = to.url.href;
		}
	});
</script>

<svelte:head>
	<meta name="traceparent" content={data.traceparent} />
	{#if !USE_PERMALINK}
		<script src="https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js"></script>
		<script src="https://js.stripe.com/v3/"></script>
	{/if}
</svelte:head>

<div class="fixed inset-0"><slot></slot></div>
